import { useContext, useCallback } from 'react';
import { Box, Typography, Link, TextButton, styled } from 'bonfire-kindling';
import Axios from 'helpers/axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CUSTOMERS_ME, SUBSCRIPTIONS_ID } from 'constants/apis';
import { Loader } from 'components';
import { AppContext, ToastContext } from 'contexts';
import { Trans, useTranslation } from 'react-i18next';
import { countries } from 'constants/regions';
import { STRIPECUSTOMERPORTALURL } from 'constants/apis';

const StyledRenewButton = styled(TextButton)(({ theme }) => ({
  background: theme.palette.primary.main,
  paddingTop: 0,
  paddingBottom: 0,
}));

const StyledEditButton = styled(TextButton)(({ theme }) => ({
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
  color: theme.palette.primary.main,
}));

// Formats a Stripe billing address.
const BillingAddress = ({ stripeAddress }) => {
  if (stripeAddress === null) {
    return null;
  }

  const match = countries.find(({ value }) => value === stripeAddress.country);

  const countryLabel = match !== undefined ? match.label : stripeAddress.country;

  return (
    <>
      <Typography>{stripeAddress.line1}</Typography>
      {stripeAddress.line2 === '' || stripeAddress.line2 === null ? null : (
        <Typography>{stripeAddress.line2}</Typography>
      )}
      <Typography>
        {stripeAddress.city}, {stripeAddress.state} {stripeAddress.postal_code}
      </Typography>
      <Typography>{countryLabel}</Typography>
    </>
  );
};
const SUBSCRIPTIONS_QUERY_KEY = ['subscriptions', 'me'];

export default () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { triggerToast } = useContext(ToastContext);
  const appContext = useContext(AppContext);
  const { isLoading: customerLoading, data: customerDetails } = useQuery(
    ['customers', 'me'],
    () => Axios.get(CUSTOMERS_ME).then(({ data }) => data),
    {
      onError: () => triggerToast({ message: t('generic_error') }),
      staleTime: 5 * 60 * 1000,
    }
  );

  const { isLoading: subscriptionLoading, data: stripeSubscription } = useQuery(
    SUBSCRIPTIONS_QUERY_KEY,
    () => Axios.get(SUBSCRIPTIONS_ID('me')).then(({ data }) => data),
    {
      onError: () => triggerToast({ message: t('generic_error') }),
      staleTime: 5 * 60 * 1000,
    }
  );

  const { mutate: handleChangeAutoRenew, isLoading: isChangingAutoRenew } = useMutation(
    hasAutoRenew =>
      Axios.patch(SUBSCRIPTIONS_ID(appContext.subscriptionId), {
        cancel_at_period_end: !hasAutoRenew,
      }),
    {
      onSuccess: ({ data }) => {
        queryClient.setQueryData(SUBSCRIPTIONS_QUERY_KEY, () => data);
      },
    }
  );

  const { mutate: getStripeCustomerPortalUrl } = useMutation(() =>
    Axios.get(STRIPECUSTOMERPORTALURL)
  );
  const goToStripeCustomerPortal = useCallback(() => {
    return getStripeCustomerPortalUrl(null, {
      onSuccess: ({ data }) => window.location.assign(data.url),
      onError: () => triggerToast({ message: t('generic_error') }),
    });
  }, [getStripeCustomerPortalUrl, t, triggerToast]);

  if (customerLoading || subscriptionLoading) {
    return <Loader variant="puff" isCentered />;
  }

  // When a User first signs up for PV, we set the User's stripeCustomer.default_source to be
  // the credit card the User signed up with. Stripe, however, is deprecating the "Sources" API
  // in favour of payment methods/intents (https://stripe.com/docs/payments/older-apis). If a
  // User adds a new credit card for PV (via the Stripe Customer Portal) and sets the new card
  // as the default, the card is created as a PaymentMethod and is set as the
  // stripeCustomer.invoice_settings.default_payment_method. So the User's stripeCustomer may
  // have both a non-null default_source and a non-null invoice_settings.default_payment_method.
  // It has been confirmed with the Stripe support team that a subscription invoice will use the
  // stripeCustomer's invoice_settings.default_payment_method and not the stripeCustomer's
  // default_source if they are both non-null (i.e. invoice_settings.default_payment_method
  // takes precedence over default_source).
  const paymentMethod =
    customerDetails.invoice_settings.default_payment_method !== null
      ? customerDetails.invoice_settings.default_payment_method.card
      : customerDetails.default_source;

  const autoRenewDate = new Date(stripeSubscription.current_period_end * 1000).toDateString();

  if (stripeSubscription.cancel_at_period_end) {
    return (
      <>
        <Box mb={3}>
          <StyledRenewButton
            variant="contained"
            color="primary"
            onClick={() =>
              handleChangeAutoRenew(true, {
                onError: () => {
                  triggerToast({ message: t('settings.subscription.auto_renew_error') });
                },
              })
            }
            isLoading={isChangingAutoRenew}
          >
            {t('settings.subscription.resume')}
          </StyledRenewButton>
        </Box>
        <Typography variant="body1">
          {t('settings.subscription.resume_message')}
          <strong>{autoRenewDate}</strong>
        </Typography>
      </>
    );
  }

  return (
    <>
      <Box mb={3}>
        <Box display="flex" mb={1}>
          <Typography variant="h3" component="h3">
            {t('billing_info.title')}
          </Typography>
          <Box ml={1}>
            <StyledEditButton
              role="link"
              variant="text"
              onClick={goToStripeCustomerPortal}
              aria-label="Edit Billing Address"
              label="Edit"
            />
          </Box>
        </Box>
        <BillingAddress stripeAddress={customerDetails.address} />
      </Box>
      <Box mb={3}>
        <Box display="flex" mb={1}>
          <Typography variant="h3" component="h3">
            {t('settings.subscription.payment_method')}
          </Typography>
          <Box ml={1}>
            <StyledEditButton
              role="link"
              variant="text"
              onClick={goToStripeCustomerPortal}
              aria-label="Edit Payment Method"
              label="Edit"
            />
          </Box>
        </Box>
        <Box>
          {paymentMethod ? (
            <>
              <Typography>{customerDetails.name}</Typography>
              <Typography>{`**** **** **** ${paymentMethod.last4}`}</Typography>
              <Typography>
                {t('settings.subscription.billing_message', {
                  amount: stripeSubscription.plan.amount / 100,
                  currency: stripeSubscription.plan.currency.toUpperCase(),
                  interval: stripeSubscription.plan.interval,
                })}
              </Typography>
            </>
          ) : (
            <Typography>{t('settings.subscription.add_payment_method')}</Typography>
          )}
        </Box>
      </Box>
      <Box display="flex" mb={3}>
        <Typography>{t('settings.subscription.renew_date', { date: autoRenewDate })}</Typography>
      </Box>
      <Box>
        <Trans i18nKey="settings.subscription.billing_update_message">
          <Typography display="inline">
            If you have any questions about your subscription, please reach out to the Bonfire
            support at
          </Typography>
          <Link href="mailto:support@gobonfire.com" rel="noopener noreferrer">
            support@gobonfire.com
          </Link>
        </Trans>
      </Box>
    </>
  );
};
